/* Import components */

import React, { useEffect, useState } from 'react';
import { DataTablePaginated } from '../components'; // Adjust the path if necessary
import { axios } from '../libs/axios';
import ReactPaginate from 'react-paginate';
import { useSelector } from 'react-redux';
import { User } from '../states/actions';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '../i18n/I18nContext';
import logger from '../utils/logger';
import { generateRolling12Months, resolveMonthYearParams } from '../utils/periodFilters';

const INVOICE_STORAGE_KEYS = {
	month: 'hugo.invoices.month',
	year: 'hugo.invoices.year',
	status: 'hugo.invoices.status',
	documentType: 'hugo.invoices.documentType',
	invoiceType: 'hugo.invoices.invoiceType',
	partner: 'hugo.invoices.partner',
};

const INVOICE_STATUSES = ['draft', 'issued', 'sent', 'pending', 'paid', 'refunded', 'failed', 'credited'];

function readStoredInvoiceFilter(key, fallback = '') {
	try {
		const value = localStorage.getItem(key);
		return value !== null && value !== '' ? value : fallback;
	} catch {
		return fallback;
	}
}

function getMonthNameFromFilter(month, locale = 'en') {
	if (!month) return '';
	const localeTag = locale === 'et' ? 'et-EE' : 'en-US';
	const monthIndex = month.includes('-')
		? parseInt(month.split('-')[1], 10) - 1
		: parseInt(month, 10) - 1;
	return new Date(0, monthIndex).toLocaleString(localeTag, { month: 'long' }).toLowerCase();
}

/* Format date */

function formatDateDMY(date) {
	if (!date) return '';
	const d     = new Date(date);
	const day   = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year  = d.getFullYear();
	return `${day}.${month}.${year}`;
}

/* Invoice type filter */

const InvoiceTypeFilter = ({ typeFilter, onChange, t }) => {
	const opts = [
		{ label: t('invoices.marketplace'),  value:'marketplace' },
		{ label: t('invoices.subscription'), value:'subscription' },
	];
	return (
		<>
			{opts.map(o => {
				const sel = typeFilter === o.value;
				return (
					<div
						key={o.value}
						onClick={() => onChange(o.value)}
						className={`status status-${o.value}${sel ? ' selected' : ''}`}>
						<span className="indicator"/>
						{o.label}
					</div>
				);
			})}
		</>
	);
};

/* Export invoices */

export const Invoices = () => {
	const user = useSelector(User.get());
	const { t, locale } = useTranslation();
	const history = useHistory();
	logger.log('🔍 Invoices component - User:', { role: user.role, user_no: user.user_no });
	const [rawInvoices, setRawInvoices]   = useState([]);
	const [invoices, setInvoices]         = useState([]);
	const [loading, setLoading]           = useState(false);
	const [currentPage, setCurrentPage]   = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [sizePerPage, setSizePerPage]   = useState(100); // Default to 25 items per page
	const [month, setMonth]               = useState(() => readStoredInvoiceFilter(INVOICE_STORAGE_KEYS.month, ''));
	const [year, setYear]                 = useState(() => readStoredInvoiceFilter(INVOICE_STORAGE_KEYS.year, ''));
	const [partnerFilter, setPartnerFilter] = useState(() => readStoredInvoiceFilter(INVOICE_STORAGE_KEYS.partner, ''));
	const [invoiceTypeFilter, setInvoiceTypeFilter] = useState(() => readStoredInvoiceFilter(INVOICE_STORAGE_KEYS.invoiceType, 'marketplace'));
	const [documentTypeFilter, setDocumentTypeFilter] = useState(() => readStoredInvoiceFilter(INVOICE_STORAGE_KEYS.documentType, 'all'));
	const [statusFilter, setStatusFilter] = useState(() => readStoredInvoiceFilter(INVOICE_STORAGE_KEYS.status, 'all'));
	const [tooltip,setTooltip]            = useState({visible:false,text:'',x:0,y:0});
	const [search, setSearch]             = useState('');
	const [sortField, setSortField]       = useState('createdAt');
	const [sortOrder, setSortOrder]       = useState('desc');
	const [syncingReceipts, setSyncingReceipts] = useState(false);
	const [syncingInvoices, setSyncingInvoices] = useState(false);
	const [invoiceSheetGid, setInvoiceSheetGid] = useState(null);
	const [receiptSheetGid, setReceiptSheetGid] = useState(null);
	const [spreadsheetUrl, setSpreadsheetUrl] = useState(null);
	const [totals, setTotals] = useState(null);
	const [totalsLoading, setTotalsLoading] = useState(false);

	// Fetch spreadsheet URL from settings on mount
	useEffect(() => {
		const fetchSpreadsheetUrl = async () => {
			try {
				const response = await axios.get('/crm/settings');
				const data = response.data || response;
				if (data.spreadsheetUrl) {
					setSpreadsheetUrl(data.spreadsheetUrl);
				} else if (data.spreadsheetId) {
					// Fallback: construct URL from ID if URL not available
					setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit`);
				}
			} catch (error) {
				logger.error('Error fetching spreadsheet URL:', error);
			}
		};
		fetchSpreadsheetUrl();
	}, []);

	const [editModalVisible, setEditModalVisible] = useState(false);
	const [editInvoice, setEditInvoice] = useState({});
	
	// Add Receipt modal state
	const [receiptModalVisible, setReceiptModalVisible] = useState(false);
	const [receiptInvoice, setReceiptInvoice] = useState(null);
	const [receiptData, setReceiptData] = useState({
		amount: '',
		receipt_date: '',
		payment_method: ''
	});
	const [maxAmount, setMaxAmount] = useState(0);
	
	/* Admin dropdowns data */
	const [crmUsers, setCrmUsers] = useState([]);
	const [projects, setProjects] = useState([]);
	const [clients, setClients] = useState([]);
	const [dropdownsLoading, setDropdownsLoading] = useState(false);
	
	/* Project search functionality */
	const [projectSearch, setProjectSearch] = useState('');
	const [filteredProjects, setFilteredProjects] = useState([]);
	const [showProjectDropdown, setShowProjectDropdown] = useState(false);
	
	/* Client search functionality */
	const [clientSearch, setClientSearch] = useState('');
	const [filteredClients, setFilteredClients] = useState([]);
	const [showClientDropdown, setShowClientDropdown] = useState(false);
	
	/* Fetch invoices with pagination and filters */
	
	const fetchInvoices = async (page=1, sizePerPage=100, q='', month='', year='', partner='', sortField='createdAt', sortOrder='desc', invoiceType='', documentType='', status='all') => {
		try {
			setLoading(true);
			const { year: yearParam, month: monthParam } = resolveMonthYearParams(month, year);
			const response = await axios.get('/invoices', {
				params: {
					page,
					sizePerPage,
					q,
					month: monthParam,
					year: yearParam,
					partner,
					sortField,
					sortOrder,
					invoiceType,
					documentType,
					status,
				}
			});
			setRawInvoices(response.invoices);
			setInvoices(response.invoices);
			setTotalRecords(response.total);
			setTotals(response.totals ?? null);
		} finally {
			setLoading(false);
		}
	};

	const fetchTotals = async () => {
		setTotalsLoading(true);
		try {
			const { year: yearParam, month: monthParam } = resolveMonthYearParams(month, year);
			const body = await axios.get('/invoices/totals', {
				params: {
					q: search,
					month: monthParam,
					year: yearParam,
					partner: partnerFilter,
					invoiceType: invoiceTypeFilter,
					documentType: documentTypeFilter,
					status: statusFilter,
				},
			});
			if (body?.status && body.status >= 400) return;
			setTotals(body?.totals ?? null);
		} catch (err) {
			logger.error('Error fetching invoice totals:', err);
		} finally {
			setTotalsLoading(false);
		}
	};
	
	/* Fetch dropdown data for admin users */
	const fetchDropdownData = async () => {
		if (user && user.role === 'admin') {
			try {
				setDropdownsLoading(true);
				const [usersResponse, projectsResponse, clientsResponse] = await Promise.all([
					axios.get('/projects/crm-users/all'),
					axios.get('/projects', { params: { sizePerPage: 1000 } }),
					axios.get('/clients', { params: { sizePerPage: 10000 } })
				]);
				setCrmUsers(usersResponse);
				setProjects(projectsResponse.projects || []);
				setClients(clientsResponse.clients || []);
			} catch (error) {
				logger.error('Error fetching dropdown data:', error);
			} finally {
				setDropdownsLoading(false);
			}
		}
	};
	
	/* Project search functionality */
	const handleProjectSearch = (searchTerm) => {
		setProjectSearch(searchTerm);
		
		if (searchTerm.trim() === '') {
			setFilteredProjects(projects);
			setShowProjectDropdown(false);
			return;
		}
		
		const filtered = projects.filter(project => {
			const projectNo = project.project_no?.toString().toLowerCase() || '';
			const projectName = project.project_name?.toLowerCase() || '';
			const clientName = project.client_name?.toLowerCase() || '';
			const search = searchTerm.toLowerCase();
			
			return projectNo.includes(search) || 
				   projectName.includes(search) || 
				   clientName.includes(search);
		});
		
		setFilteredProjects(filtered);
		setShowProjectDropdown(filtered.length > 0);
	};
	
	const selectProject = (project) => {
		setEditInvoice({ 
			...editInvoice, 
			related_project_id: project._id,
			related_project_no: project.project_no
		});
		setProjectSearch(`#${project.project_no} - ${project.project_name} (${project.client_name})`);
		setShowProjectDropdown(false);
	};
	
	const clearProjectSelection = () => {
		setEditInvoice({ 
			...editInvoice, 
			related_project_id: '',
			related_project_no: ''
		});
		setProjectSearch('');
		setShowProjectDropdown(false);
	};
	
	/* Client search functionality */
	const handleClientSearch = (searchTerm) => {
		setClientSearch(searchTerm);
		
		if (searchTerm.trim() === '') {
			setFilteredClients(clients);
			setShowClientDropdown(false);
		} else {
			const searchLower = searchTerm.toLowerCase();
			const filtered = clients.filter(client => {
				const fullName = `${client.firstname} ${client.lastname}`.toLowerCase();
				const email = (client.email || '').toLowerCase();
				const businessName = (client.business_name || '').toLowerCase();
				return fullName.includes(searchLower) || email.includes(searchLower) || businessName.includes(searchLower);
			}).sort((a, b) => {
				const aFullName = `${a.firstname} ${a.lastname}`;
				const bFullName = `${b.firstname} ${b.lastname}`;
				return aFullName.localeCompare(bFullName);
			});
			
			setFilteredClients(filtered);
			setShowClientDropdown(true);
		}
	};
	
	const selectClient = (client) => {
		const isBusiness = client.type === 'business';
		setEditInvoice({
			...editInvoice,
			client_id: client._id,
			client_name: `${client.firstname} ${client.lastname}`,
			client_type: client.type === 'business' ? 'business' : 'individual',
			business_name: client.business_name || '',
			business_address: client.business_address || '',
			business_reg_code: client.business_reg_code || ''
		});
		
		setClientSearch(isBusiness ? 
			`${client.business_name} (${client.firstname} ${client.lastname})` :
			`${client.firstname} ${client.lastname}`
		);
		setShowClientDropdown(false);
	};
	
	const clearClientSelection = () => {
		setEditInvoice({ 
			...editInvoice, 
			client_id: '',
			client_name: '',
			client_type: 'individual',
			business_name: '',
			business_address: '',
			business_reg_code: ''
		});
		setClientSearch('');
		setShowClientDropdown(false);
	};

	/* Handle table sorting */
	
	const handleTableChange = (type, { sortField, sortOrder }) => {
		logger.log('handleTableChange called:', { type, sortField, sortOrder, currentPage });
		logger.log('Current sort state:', { sortField, sortOrder });
		if (type === 'sort') {
			logger.log('Sort event - resetting page to 1');
			logger.log('Setting sortField to:', sortField);
			logger.log('Setting sortOrder to:', sortOrder);
			setSortField(sortField);
			setSortOrder(sortOrder);
			setCurrentPage(1); // Reset to first page when sorting
		}
	};

	/* Total Row Component */

	const buildInvoiceTotalRowData = (totals, onFetchTotals, totalsLoading) => {
		const baseRow = {
			client_name: <></>,
			...(user.role === 'admin' && (invoiceTypeFilter !== 'subscription') ? { partner_name: <></> } : {}),
			...(user.role === 'lawyer' || invoiceTypeFilter !== 'subscription' ? { related_project_no: <></> } : {}),
			...(user.role === 'admin' && invoiceTypeFilter !== 'marketplace' ? { related_subscription_id: <></> } : {}),
			status: <></>,
			payment_transactions: <></>,
			hourly_rate: <></>,
			payment_description: <></>,
			createdAt: <></>,
			...(user.role === 'admin' ? { edit_delete: <></> } : {})
		};

		if (totals === null) {
			return {
				actions: (
					<div className='actions'>
						<button
							type='button'
							className='button button-small'
							onClick={(e) => { e.stopPropagation(); onFetchTotals?.(); }}
							disabled={totalsLoading}
						>
							{t('common.totals')} <i className='fa fa-solid fa-rotate-right' aria-hidden='true' />
						</button>
					</div>
				),
				invoiceNumber: <></>,
				amount_total: '',
				billable_hours: '',
				...baseRow
			};
		}

		return {
			actions: <></>,
			invoiceNumber: <></>,
			amount_total: <strong>{totals.total_amount ? `${parseFloat(totals.total_amount).toFixed(2)} €` : '€0.00'}</strong>,
			billable_hours: <strong>{totals.total_hours || '-'}</strong>,
			...baseRow
		};
	};

	/* Fetch invoices on mount and when filters/page/sizePerPage change */

	useEffect(() => { 
		fetchInvoices(currentPage, sizePerPage, search, month, year, partnerFilter, sortField, sortOrder, invoiceTypeFilter, documentTypeFilter, statusFilter);
		fetchDropdownData();
	}, [currentPage, sizePerPage, search, month, year, partnerFilter, sortField, sortOrder, invoiceTypeFilter, documentTypeFilter, statusFilter]);

	useEffect(() => {
		try {
			if (month) {
				localStorage.setItem(INVOICE_STORAGE_KEYS.month, month);
			} else {
				localStorage.removeItem(INVOICE_STORAGE_KEYS.month);
			}
		} catch {
			// ignore storage errors
		}
	}, [month]);

	useEffect(() => {
		try {
			if (year) {
				localStorage.setItem(INVOICE_STORAGE_KEYS.year, year);
			} else {
				localStorage.removeItem(INVOICE_STORAGE_KEYS.year);
			}
		} catch {
			// ignore storage errors
		}
	}, [year]);

	useEffect(() => {
		try {
			localStorage.setItem(INVOICE_STORAGE_KEYS.status, statusFilter);
		} catch {
			// ignore storage errors
		}
	}, [statusFilter]);

	useEffect(() => {
		try {
			localStorage.setItem(INVOICE_STORAGE_KEYS.documentType, documentTypeFilter);
		} catch {
			// ignore storage errors
		}
	}, [documentTypeFilter]);

	useEffect(() => {
		try {
			localStorage.setItem(INVOICE_STORAGE_KEYS.invoiceType, invoiceTypeFilter);
		} catch {
			// ignore storage errors
		}
	}, [invoiceTypeFilter]);

	useEffect(() => {
		try {
			if (partnerFilter) {
				localStorage.setItem(INVOICE_STORAGE_KEYS.partner, partnerFilter);
			} else {
				localStorage.removeItem(INVOICE_STORAGE_KEYS.partner);
			}
		} catch {
			// ignore storage errors
		}
	}, [partnerFilter]);
	
	/* Handle click outside for project dropdown */
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest('.project-search-container')) {
				setShowProjectDropdown(false);
			}
			if (!event.target.closest('.client-search-container')) {
				setShowClientDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);
	
	/* Search fallback */
	
useEffect(() => {
	const s = search.trim().toLowerCase();

	if (!rawInvoices || !Array.isArray(rawInvoices)) {
		setInvoices([]);
		return;
	}

	const filtered = rawInvoices.filter(i => {
		// Apply search filter (only if search term exists)
		let matchesSearch = true;
		if (s) {
			const createdDate = i.createdAt ? formatDateDMY(i.createdAt).toLowerCase() : '';
			const receiptDate = i.receipt_date ? formatDateDMY(i.receipt_date).toLowerCase() : '';
			const invoiceNumber = i.invoiceNumber?.toString().toLowerCase() || '';
			const amount = i.amount_total?.toString() || '';
			const clientName = i.client_name?.toLowerCase() || '';
			const projectNo = i.related_project_no?.toString() || '';
			const partnerName = i.partner_name?.toLowerCase() || '';
			const partnerCompanyName = i.partner_company_name?.toLowerCase() || '';

			matchesSearch = invoiceNumber.includes(s)
				|| amount.includes(s)
				|| clientName.includes(s)
				|| projectNo.includes(s)
				|| receiptDate.includes(s)
				|| createdDate.includes(s)
				|| partnerName.includes(s)
				|| partnerCompanyName.includes(s);
		}

		// Apply invoice type filter (only for admin users)
		let matchesTypeFilter = true;
		if (user.role === 'admin') {
			if (invoiceTypeFilter === 'marketplace') {
				// Marketplace invoices are those without related_subscription_id or with empty subscription
				matchesTypeFilter = !i.related_subscription_id || i.related_subscription_id === '';
			} else if (invoiceTypeFilter === 'subscription') {
				// Subscription invoices are those with related_subscription_id that has a value
				matchesTypeFilter = !!(i.related_subscription_id && i.related_subscription_id !== '');
			}
		}
		// For lawyer users, matchesTypeFilter remains true (show all invoices)

		// Apply document type filter (prepayment/invoice/credit)
		let matchesDocumentType = true;
		if (documentTypeFilter !== 'all') {
			matchesDocumentType = i.type === documentTypeFilter;
		}

		return matchesSearch && matchesTypeFilter && matchesDocumentType;
	});

	setInvoices(filtered);
}, [search, rawInvoices, invoiceTypeFilter, documentTypeFilter]);
	
	
	/* Handle downloading filtered data */
	
	const handleDownloadPDF = (invoiceId) => {
		// Create a direct download link without authentication
		let baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';
		
		// Remove trailing /api if it exists to avoid double /api
		if (baseUrl.endsWith('/api')) {
			baseUrl = baseUrl.replace('/api', '');
		}
		
		const pdfUrl = `${baseUrl}/api/invoices/pdf/${invoiceId}`;
		logger.log('PDF URL:', pdfUrl);
		window.open(pdfUrl, '_blank');
	};
	
	const handleDownloadCSV = async () => {
		try {
			const { year: yearParam, month: monthParam } = resolveMonthYearParams(month, year);
			logger.log('Starting CSV download with params:', { month: monthParam, year: yearParam, search, partnerFilter, invoiceTypeFilter, statusFilter, sortField, sortOrder });
			
			let response;
			
			try {
				// Use axios (same as other requests in this component) with blob response
				logger.log('Making axios request to /invoices/download/csv');
				response = await axios.get('/invoices/download/csv', {
					params: { 
						month: monthParam, 
						year: yearParam,
						q: search,
						partner: partnerFilter,
						invoiceType: user.role === 'admin' ? invoiceTypeFilter : undefined,
						documentType: documentTypeFilter,
						status: statusFilter,
						sortField,
						sortOrder
					},
					responseType: 'blob',
				});
				
				logger.log('Raw axios response:', response);
				logger.log('Response type:', typeof response);
				logger.log('Response properties:', Object.keys(response));
				
				// If response is directly a blob (some axios configurations do this)
				if (response instanceof Blob) {
					logger.log('Response is directly a Blob:', { size: response.size, type: response.type });
					response = { data: response, status: 200, headers: { 'content-type': response.type } };
				}
				
			} catch (axiosError) {
				logger.error('Axios request failed:', axiosError);
				
				// Try with text response type as fallback
				logger.log('Trying with text response type...');
				try {
					response = await axios.get('/invoices/download/csv', {
						params: { 
							month: monthParam, 
							year: yearParam,
							q: search,
							partner: partnerFilter,
							invoiceType: user.role === 'admin' ? invoiceTypeFilter : undefined,
							documentType: documentTypeFilter,
							status: statusFilter,
							sortField,
							sortOrder
						},
						responseType: 'text',
					});
					
					logger.log('Text response received:', {
						status: response?.status,
						dataLength: response?.data?.length,
						dataPreview: response?.data?.substring(0, 100)
					});
					
					// Convert text to blob
					if (response?.data) {
						const textBlob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
						response.data = textBlob;
					}
					
				} catch (textError) {
					logger.error('Text request also failed:', textError);
					throw new Error('Both blob and text requests failed: ' + textError.message);
				}
			}
			
			if (!response) {
				throw new Error('No response received from server');
			}
			
			logger.log('CSV Response received:', {
				status: response.status,
				contentType: response.headers?.['content-type'] || 'unknown',
				dataSize: response.data?.size || 'unknown',
				dataType: response.data?.type || 'unknown',
				headers: response.headers
			});
			
			// Check if we got HTML instead of CSV (authentication issue)
			const contentType = response.headers?.['content-type'] || '';
			if (contentType.includes('text/html')) {
				throw new Error('Authentication failed - got HTML page instead of CSV');
			}
			
			// Check if response is actually a blob with content
			if (!response.data || (response.data.size !== undefined && response.data.size === 0)) {
				throw new Error('Empty response received from server');
			}
			
			const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
			logger.log('Created blob:', { size: blob.size, type: blob.type });
			
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;

			// Create filename based on current filters
			let filename = '';
			let filenameParts = [];
			
			// Add invoice type if admin user (lawyers see all invoices)
			if (user.role === 'admin' && invoiceTypeFilter) {
				filenameParts.push(invoiceTypeFilter);
			}
			
			// Add month if selected
			if (month) {
				filenameParts.push(getMonthNameFromFilter(month, locale));
			}
			
			// Add partner if selected
			if (partnerFilter) {
				const selectedPartner = crmUsers.find(p => p.user_no === partnerFilter);
				if (selectedPartner) {
					const partnerName = selectedPartner.firstName && selectedPartner.lastName 
						? `${selectedPartner.firstName}-${selectedPartner.lastName}`.toLowerCase().replace(/\s+/g, '-')
						: 'partner';
					filenameParts.push(partnerName);
				}
			}
			
			// Build final filename
			if (filenameParts.length > 0) {
				filename = `${filenameParts.join('-')}-invoicing-hugo.csv`;
			} else {
				filename = `invoicing-hugo.csv`;
			}

			logger.log('Downloading file:', filename);
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			
			window.URL.revokeObjectURL(url);
			
			window.nSuccess({
				title: t('common.successTitle'), 
				text: t('invoices.csvDownloadSuccess', { filename }), 
				icon: 'fa-check'
			});
			
		} catch (error) {
			logger.error('Error downloading CSV file:', error);
			logger.error('Error stack:', error.stack);
			
			// If it's an auth error, show specific message
			if (error.message && error.message.includes('Authentication failed')) {
				window.nError({
					title: t('common.authError'), 
					text: t('common.authErrorText'), 
					icon: 'fa-exclamation-triangle'
				});
			} else {
				window.nError({
					title: t('common.downloadError'), 
					text: t('invoices.csvDownloadError', { message: error.message }), 
					icon: 'fa-exclamation-triangle'
				});
			}
		}
	};
	
	const handleSyncInvoicesToSheets = async () => {
		try {
			setSyncingInvoices(true);
			const response = await axios.post('/invoices/sync-invoices-to-sheets');
			
			// Handle response - axios might return data directly or nested
			const responseData = response.data || response;
			const message = responseData?.message || t('invoices.syncInvoicesSuccess');
			
			// Store the spreadsheet URL for direct link
			if (responseData?.spreadsheetUrl) {
				setSpreadsheetUrl(responseData.spreadsheetUrl);
			} else if (responseData?.spreadsheetId) {
				// Fallback: construct URL from ID if URL not available
				setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${responseData.spreadsheetId}/edit`);
			}
			if (responseData?.sheetGid) {
				setInvoiceSheetGid(responseData.sheetGid);
			}
			
			window.nSuccess({
				title: t('common.successTitle'),
				text: message,
				icon: 'fa-check'
			});
		} catch (error) {
			logger.error('Error syncing invoices to sheets:', error);
			const errorMessage = error?.response?.data?.message || error?.message || t('invoices.syncInvoicesError');
			window.nError({
				title: t('common.syncError'),
				text: errorMessage,
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setSyncingInvoices(false);
		}
	};

	const handleSyncReceiptsToSheets = async () => {
		try {
			setSyncingReceipts(true);
			const response = await axios.post('/invoices/sync-receipts-to-sheets');
			
			// Handle response - axios might return data directly or nested
			const responseData = response.data || response;
			const message = responseData?.message || t('invoices.syncReceiptsSuccess');
			
			// Store the spreadsheet URL for direct link
			if (responseData?.spreadsheetUrl) {
				setSpreadsheetUrl(responseData.spreadsheetUrl);
			} else if (responseData?.spreadsheetId) {
				// Fallback: construct URL from ID if URL not available
				setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${responseData.spreadsheetId}/edit`);
			}
			if (responseData?.sheetGid) {
				setReceiptSheetGid(responseData.sheetGid);
			}
			
			window.nSuccess({
				title: t('common.successTitle'),
				text: message,
				icon: 'fa-check'
			});
		} catch (error) {
			logger.error('Error syncing receipts to sheets:', error);
			const errorMessage = error?.response?.data?.message || error?.message || t('invoices.syncReceiptsError');
			window.nError({
				title: t('common.syncError'),
				text: errorMessage,
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setSyncingReceipts(false);
		}
	};

	const handleDownloadTransactionsCSV = async () => {
		try {
			const { year: yearParam, month: monthParam } = resolveMonthYearParams(month, year);
			logger.log('Starting Transactions CSV download with params:', { month: monthParam, year: yearParam, search, partnerFilter, invoiceTypeFilter, statusFilter, sortField, sortOrder });
			
			let response;
			
			try {
				// Use axios with blob response
				logger.log('Making axios request to /invoices/download/transactions-csv');
				response = await axios.get('/invoices/download/transactions-csv', {
					params: { 
						month: monthParam, 
						year: yearParam,
						q: search,
						partner: partnerFilter,
						invoiceType: user.role === 'admin' ? invoiceTypeFilter : undefined,
						documentType: documentTypeFilter,
						status: statusFilter,
						sortField,
						sortOrder
					},
					responseType: 'blob',
				});
				
				logger.log('Raw axios response:', response);
				
				// If response is directly a blob (some axios configurations do this)
				if (response instanceof Blob) {
					logger.log('Response is directly a Blob:', { size: response.size, type: response.type });
					response = { data: response, status: 200, headers: { 'content-type': response.type } };
				}
				
			} catch (axiosError) {
				logger.error('Axios request failed:', axiosError);
				
				// Try with text response type as fallback
				logger.log('Trying with text response type...');
				try {
					response = await axios.get('/invoices/download/transactions-csv', {
						params: { 
							month: monthParam, 
							year: yearParam,
							q: search,
							partner: partnerFilter,
							invoiceType: user.role === 'admin' ? invoiceTypeFilter : undefined,
							documentType: documentTypeFilter,
							status: statusFilter,
							sortField,
							sortOrder
						},
						responseType: 'text',
					});
					
					logger.log('Text response received:', {
						status: response?.status,
						dataLength: response?.data?.length,
						dataPreview: response?.data?.substring(0, 100)
					});
					
					// Convert text to blob
					if (response?.data) {
						const textBlob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
						response.data = textBlob;
					}
					
				} catch (textError) {
					logger.error('Text request also failed:', textError);
					throw new Error('Both blob and text requests failed: ' + textError.message);
				}
			}
			
			if (!response) {
				throw new Error('No response received from server');
			}
			
			logger.log('Transactions CSV Response received:', {
				status: response.status,
				contentType: response.headers?.['content-type'] || 'unknown',
				dataSize: response.data?.size || 'unknown',
				dataType: response.data?.type || 'unknown',
				headers: response.headers
			});
			
			// Check if we got HTML instead of CSV (authentication issue)
			const contentType = response.headers?.['content-type'] || '';
			if (contentType.includes('text/html')) {
				throw new Error('Authentication failed - got HTML page instead of CSV');
			}
			
			// Check if response is actually a blob with content
			if (!response.data || (response.data.size !== undefined && response.data.size === 0)) {
				throw new Error('Empty response received from server');
			}
			
			const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
			logger.log('Created blob:', { size: blob.size, type: blob.type });
			
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;

			// Create filename based on current filters
			let filename = '';
			let filenameParts = [];
			
			// Add invoice type if admin user
			if (user.role === 'admin' && invoiceTypeFilter) {
				filenameParts.push(invoiceTypeFilter);
			}
			
			// Add month if selected
			if (month) {
				filenameParts.push(getMonthNameFromFilter(month, locale));
			}
			
			// Add partner if selected
			if (partnerFilter) {
				const selectedPartner = crmUsers.find(p => p.user_no === partnerFilter);
				if (selectedPartner) {
					const partnerName = selectedPartner.firstName && selectedPartner.lastName 
						? `${selectedPartner.firstName}-${selectedPartner.lastName}`.toLowerCase().replace(/\s+/g, '-')
						: 'partner';
					filenameParts.push(partnerName);
				}
			}
			
			// Build final filename
			if (filenameParts.length > 0) {
				filename = `${filenameParts.join('-')}-transactions-hugo.csv`;
			} else {
				filename = `transactions-hugo.csv`;
			}

			logger.log('Downloading file:', filename);
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			
			window.URL.revokeObjectURL(url);
			
			window.nSuccess({
				title: 'Success!', 
				text: t('invoices.csvDownloadSuccess', { filename }), 
				icon: 'fa-check'
			});
			
		} catch (error) {
			logger.error('Error downloading Transactions CSV file:', error);
			logger.error('Error stack:', error.stack);
			
			// If it's an auth error, show specific message
			if (error.message && error.message.includes('Authentication failed')) {
				window.nError({
					title: t('common.authError'), 
					text: t('common.authErrorText'), 
					icon: 'fa-exclamation-triangle'
				});
			} else {
				window.nError({
					title: t('common.downloadError'), 
					text: t('invoices.csvDownloadError', { message: error.message }), 
					icon: 'fa-exclamation-triangle'
				});
			}
		}
	};
	
	/* Table actions */


	const openEditModal = (invoice) => {
		setEditInvoice(invoice);
		// Set project search field for editing
		if (invoice.related_project_no && invoice.related_project_id) {
			const project = projects.find(p => p._id === invoice.related_project_id);
			if (project) {
				setProjectSearch(`#${project.project_no} - ${project.project_name} (${project.client_name})`);
			} else {
				setProjectSearch(`#${invoice.related_project_no}`);
			}
		} else {
			setProjectSearch('');
		}
		
		// Set client search field for editing
		if (invoice.client_name) {
			if (invoice.client_type === 'business' && invoice.business_name) {
				setClientSearch(`${invoice.business_name} (${invoice.client_name})`);
			} else {
				setClientSearch(invoice.client_name);
			}
		} else {
			setClientSearch('');
		}
		
		setEditModalVisible(true);
	};

	const handlePartnerUpdate = async (e) => {
		e.preventDefault();
		try {
			// Prepare update data
			const updateData = {
				related_partner_id: editInvoice.related_partner_id,
				partner_name: editInvoice.partner_name,
				partner_company_name: editInvoice.partner_company_name,
				related_project_id: editInvoice.related_project_id,
				related_project_no: editInvoice.related_project_no,
				// Client fields
				client_id: editInvoice.client_id,
				client_name: editInvoice.client_name,
				client_type: editInvoice.client_type,
				business_name: editInvoice.business_name,
				business_address: editInvoice.business_address,
				business_reg_code: editInvoice.business_reg_code,
			};

			// Payment fields removed - now handled through Add Receipt modal

		await axios.put(`/invoices/${editInvoice._id}`, updateData);
		setEditModalVisible(false);
		fetchInvoices(currentPage, sizePerPage, search, month, year, partnerFilter, sortField, sortOrder, invoiceTypeFilter, documentTypeFilter, statusFilter);
			
			window.nSuccess && window.nSuccess({
				title: t('common.successTitle'), 
				text: t('invoices.updateSuccess'), 
				icon: 'fa-check-square'
			});
		} catch (err) {
			logger.error("Failed to update invoice", err);
			window.nError && window.nError({
				title: t('common.errorTitle'), 
				text: t('invoices.updateError'), 
				icon: 'fa-exclamation-triangle'
			});
		}
	};

	
	const confirmDelete = (text, onYes) => {
		let n; n = window.nConfirm({
			title: t('invoices.deleteConfirm'),
			text,
			icon: 'fa-exclamation-circle',
			onYes: async () => { await onYes(); n?.close(); },
			onNo: () => { n?.close(); }
		});
	};
	
	const deleteInvoice = (invoiceId,invoiceNumber) => {
		confirmDelete(`<span>${invoiceNumber}</span>`, async () => {
			try {
				await axios.delete(`/invoices/${invoiceId}`);
				setInvoices((prev) => prev.filter((invoice) => invoice._id !== invoiceId));
				setTotalRecords((prev) => prev - 1);
			} catch (error) {
				logger.error('Error deleting invoice:', error);
			}
		});
	};
	
	// Calculate amount due for an invoice
	const calculateAmountDue = (invoice) => {
		if (!invoice.payment_transactions || invoice.payment_transactions.length === 0) {
			return invoice.amount_total || 0;
		}
		const totalPayments = invoice.payment_transactions.reduce((sum, transaction) => {
			return sum + (parseFloat(transaction.amount) || 0);
		}, 0);
		return (invoice.amount_total || 0) - totalPayments;
	};
	
	// Open Add Receipt modal
	const openReceiptModal = (invoice) => {
		setReceiptInvoice(invoice);
		const amountDue = calculateAmountDue(invoice);
		setMaxAmount(amountDue);
		setReceiptData({
			amount: amountDue > 0 ? amountDue.toString() : '',
			receipt_date: new Date().toISOString().split('T')[0],
			payment_method: ''
		});
		setReceiptModalVisible(true);
	};
	
	// Handle receipt submission
	const handleAddReceipt = async (e) => {
		e.preventDefault();
		try {
			const amount = parseFloat(receiptData.amount);
			
			// Validate amount
			if (!amount || amount <= 0) {
				window.nError && window.nError({
					title: t('common.errorTitle'), 
					text: t('invoices.paymentAmountRequired'), 
					icon: 'fa-exclamation-triangle'
				});
				return;
			}
			
			if (amount > maxAmount) {
				window.nError && window.nError({
					title: t('common.errorTitle'), 
					text: t('invoices.paymentAmountExceeds', { amount: maxAmount.toFixed(2) }), 
					icon: 'fa-exclamation-triangle'
				});
				return;
			}
			
			if (!receiptData.payment_method) {
				window.nError && window.nError({
					title: t('common.errorTitle'), 
					text: t('invoices.paymentMethodRequired'), 
					icon: 'fa-exclamation-triangle'
				});
				return;
			}
			
			if (!receiptData.receipt_date) {
				window.nError && window.nError({
					title: t('common.errorTitle'), 
					text: t('invoices.receiptDateRequired'), 
					icon: 'fa-exclamation-triangle'
				});
				return;
			}
			
			await axios.post(`/invoices/${receiptInvoice._id}/transactions`, {
				amount: amount,
				payment_method: receiptData.payment_method,
				receipt_date: receiptData.receipt_date
			});
		
		setReceiptModalVisible(false);
		fetchInvoices(currentPage, sizePerPage, search, month, year, partnerFilter, sortField, sortOrder, invoiceTypeFilter, documentTypeFilter, statusFilter);
			
			window.nSuccess && window.nSuccess({
				title: t('common.successTitle'), 
				text: t('invoices.receiptSuccess'), 
				icon: 'fa-check-square'
			});
		} catch (err) {
			logger.error("Failed to add receipt", err);
			const errorMessage = err.response?.data?.error || t('invoices.receiptError');
			window.nError && window.nError({
				title: t('common.errorTitle'), 
				text: errorMessage, 
				icon: 'fa-exclamation-triangle'
			});
		}
	};
	
	/* Table columns */
	
	const tableColumns = [
	// Actions column - PDF for all users, Edit/Add Receipt for admin only (first column, no header)
	{
		dataField: 'actions',
		text: '',
		classes: 'cell-actions',
		headerClasses: 'cell-actions',
		sort: false,
		style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		formatter: (cell, row) => (
			<div className='actions'>
			<button 
				className="button button-small" 
				onClick={() => handleDownloadPDF(row._id)}
				title={t('invoices.downloadPdf')}
			>
				{t('common.pdf')}
			</button>
			</div>
		),
	},
		{
			dataField: 'invoiceNumber',
			text: t('invoices.invoice'),
			classes: 'cell-invoice',
			headerClasses: 'cell-invoice',
			formatter: (c,r)=> {
				const canAddReceipt = user.role === 'admin' && calculateAmountDue(r) > 0;
				return (
					<div 
						className='inner'
						onClick={e => {
							e.stopPropagation();
							if (canAddReceipt) {
								openReceiptModal(r);
							}
						}}
						onMouseEnter={e => {
							if (canAddReceipt) {
								setTooltip({
									visible: true,
									text: t('invoices.addReceipt'),
									x: e.clientX,
									y: e.clientY
								});
							}
						}}
						onMouseMove={e => {
							if (canAddReceipt) {
								setTooltip({
									visible: true,
									text: t('invoices.addReceipt'),
									x: e.clientX,
									y: e.clientY
								});
							}
						}}
						onMouseLeave={() => {
							setTooltip({visible: false, text: '', x: 0, y: 0});
						}}
						style={{ 
							cursor: canAddReceipt ? 'pointer' : 'default',
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center'
						}}
					>
						{r.invoiceNumber}
					</div>
				);
			}
		},
		{
			dataField: 'amount_total',
			text: t('invoices.total'),
			classes: 'cell-total',
			headerClasses: 'cell-total',
			formatter: (value) => value != null ? `${value} €` : '',
		},
		{
			dataField: 'client_name',
			text: t('invoices.client'),
			classes: 'cell-client',
			headerClasses: 'cell-client',
			formatter: (clientName, r) => {
				const isBusiness = r.client_type === 'business';
				const displayName = isBusiness ? r.business_name : clientName;
				
				return (
					<div className='inner'>
						<div className={`client-info ${isBusiness ? 'business' : 'person'}`}>
							<span className="client-name">{displayName}</span>
							{isBusiness && <span className="client-type-badge">{t('common.business')}</span>}
						</div>
					</div>
				);
			},
		},
		// Only show partner column for admin users (and when not in subscription view for admin)
		...(user.role === 'admin' && (invoiceTypeFilter !== 'subscription') ? [{
			dataField: 'partner_name',
			text: t('invoices.partner'),
			classes: 'cell-partner',
			headerClasses: 'cell-partner',
			formatter: (c, r) => (
				<div className="inner" title={t('invoices.companyTitle', { name: r.partner_company_name || t('common.na'), id: r.related_partner_id || t('common.na') })}>
					<div style={{ fontWeight: '500' }}>
						{r.partner_name || 'N/A'}
					</div>
					{r.partner_company_name && (
						<div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
							{r.partner_company_name}
						</div>
					)}
				</div>
			),
		}] : []),
		// Only show project column when not in subscription view (for admin) or always for lawyers
		...((user.role === 'lawyer' || invoiceTypeFilter !== 'subscription') ? [{
			dataField: 'related_project_no',
			text: t('invoices.project'),
			classes: 'cell-project',
			headerClasses: 'cell-project',
			formatter: (c, r) =>
				<div
				className="inner"
				style={{ cursor: 'pointer' }}
				title={t('invoices.partnerTitle', { name: r.partner_name || t('invoices.unknownPartner'), id: r.related_partner_id || t('common.na') })}
				onClick={e => {
					e.stopPropagation();
					history.push(`/projects/${r.related_project_no}`);
				}}
				>
				{r.related_project_no}
				</div>
		}] : []),
		// Only show subscription column for admin users when not filtering for marketplace
		...(user.role === 'admin' && invoiceTypeFilter !== 'marketplace' ? [{
			dataField: 'related_subscription_id',
			text: t('invoices.subscriptionLabel'),
			classes: 'cell-client',
			headerClasses: 'cell-client',
		}] : []),
		{
			dataField: 'status',
			text: t('invoices.status'),
			classes: 'cell-client',
			headerClasses: 'cell-client',
		},
		{
			dataField: 'payment_transactions',
			text: t('invoices.transactions'),
			classes: 'cell-transactions',
			headerClasses: 'cell-transactions',
			formatter: (transactions, row) => {
				if (!transactions || transactions.length === 0) {
					return <div className="inner">-</div>;
				}
				
				return (
					<div className="inner">
						{transactions.map((txn, index) => {
							const amount = parseFloat(txn.amount || 0);
							const isNegative = amount < 0;
							const displayAmount = Math.abs(amount);
							const amountColor = isNegative ? '#d32f2f' : 'inherit';
							
							return (
								<div key={index} className="transaction-row" style={{ fontSize: '12px', marginBottom: '2px' }}>
									{txn.transaction_id}: <span style={{ color: amountColor }}>€{isNegative ? '-' : ''}{displayAmount.toFixed(2)}</span> ({txn.payment_method})
									{txn.source && <span style={{ color: '#666', marginLeft: '4px' }}>from {txn.source}</span>}
									{txn.receipt_date && <span style={{ color: '#666', marginLeft: '4px' }}>, {formatDateDMY(txn.receipt_date)}</span>}
								</div>
							);
						})}
					</div>
				);
			},
		},
		// Show hours and rate columns for all users (both admin and lawyers)
		{
			dataField: 'billable_hours',
			text: t('invoices.hours'),
			classes: 'cell-hours',
			headerClasses: 'cell-hours',
			formatter: (hours) => hours || '-',
		},
		{
			dataField: 'hourly_rate',
			text: t('invoices.rate'),
			classes: 'cell-rate',
			headerClasses: 'cell-rate',
			formatter: (rate) => rate ? `€${rate}` : '-',
		},
		{
			dataField: 'payment_description',
			text: t('invoices.description'),
			classes: 'cell-description',
			headerClasses: 'cell-description',
			style: { width: '300px', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' },
			headerStyle: { width: '300px', whiteSpace: 'normal' },
			formatter: (desc) => <div className="inner" style={{ width: '300px', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>{desc || '-'}</div>,
		},
		// Only show edit/delete for admin users
		...(user.role === 'admin' ? [{
			dataField: 'edit_delete',
			text: '',
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			formatter: (cell, row) => (
				<div className='actions'>
					<button className="button button-small" onClick={() => openEditModal(row)}>
						{t('common.edit')}
					</button>
					<button className="button button-small" onClick={() => deleteInvoice(row._id, row.invoiceNumber)}>
						{t('common.delete')}
					</button>
				</div>
			),
		}] : []),
	];
	
	/* Render HTML */
	
	return (
		<>
		<div className="row table_v2 invoices">
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>{t('invoices.title')}</h1>
						</div>
					<div className="header-actions d-flex align-items-center">
						{user && user.role === 'admin' && (
							<>
						<div style={{ position: 'relative', marginRight: '10px' }}>
							<button
								className="button button-small"
								onClick={handleSyncInvoicesToSheets}
								disabled={syncingInvoices}
								style={{ position: 'relative', paddingRight: '35px' }}
							>
								{syncingInvoices ? t('common.syncing') : t('invoices.syncInvoicesToSheets')}
							</button>
							<div
								style={{
									position: 'absolute',
									right: 0,
									top: 0,
									width: '30%',
									height: '100%',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									paddingRight: '8px',
									zIndex: 1
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (spreadsheetUrl) {
										window.open(spreadsheetUrl, '_blank');
									}
								}}
								onMouseEnter={(e) => {
									const icon = e.currentTarget.querySelector('i');
									if (icon) icon.style.opacity = '1';
								}}
								onMouseLeave={(e) => {
									const icon = e.currentTarget.querySelector('i');
									if (icon) icon.style.opacity = '0';
								}}
							>
								<i 
									className="fa-solid fa-external-link" 
									style={{ 
										fontSize: '12px', 
										opacity: 0,
										color: '#032097',
										transition: 'opacity 0.2s'
									}}
								></i>
							</div>
						</div>
						<div style={{ position: 'relative', marginRight: '10px' }}>
							<button
								className="button button-small"
								onClick={handleSyncReceiptsToSheets}
								disabled={syncingReceipts}
								style={{ position: 'relative', paddingRight: '35px' }}
							>
								{syncingReceipts ? t('common.syncing') : t('invoices.syncReceiptsToSheets')}
							</button>
							<div
								style={{
									position: 'absolute',
									right: 0,
									top: 0,
									width: '30%',
									height: '100%',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									paddingRight: '8px',
									zIndex: 1
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (spreadsheetUrl) {
										window.open(spreadsheetUrl, '_blank');
									}
								}}
								onMouseEnter={(e) => {
									const icon = e.currentTarget.querySelector('i');
									if (icon) icon.style.opacity = '1';
								}}
								onMouseLeave={(e) => {
									const icon = e.currentTarget.querySelector('i');
									if (icon) icon.style.opacity = '0';
								}}
							>
								<i 
									className="fa-solid fa-external-link" 
									style={{ 
										fontSize: '12px', 
										opacity: 0,
										color: '#032097',
										transition: 'opacity 0.2s'
									}}
								></i>
							</div>
						</div>
							</>
						)}
						<div className="action action-search">
							<input
								type="text"
								placeholder={t('common.enterKeyword')}
								value={search}
								onChange={e=>{ setSearch(e.target.value); setCurrentPage(1); }}
							/>
							<i className="fa-solid fa-magnifying-glass"></i>
						</div>
					</div>
					</div>
					<div className="x_content">
						<div className="page-container d-flex flex-column">
							{loading ? (<div>{t('common.loading')}</div>) : (
							<>
								<div className= "page-header d-flex justify-content-between align-items-center">
									<div className='filter filter-pagesize d-flex align-items-center'>
										{/* Invoice type filter - only for admin users */}
										{user.role === 'admin' && (
											<div className="filter filter-client-type" style={{marginRight: '20px'}}>
												<InvoiceTypeFilter 
													typeFilter={invoiceTypeFilter} 
													onChange={(type) => {
														setInvoiceTypeFilter(type);
														setCurrentPage(1);
													}}
													t={t}
												/>
											</div>
										)}
										{/* Document type filter (prepayment/invoice/credit) */}
										<select 
											value={documentTypeFilter} 
											onChange={(e) => {
												setDocumentTypeFilter(e.target.value);
												setCurrentPage(1);
											}}
										>
											<option value="all">{t('invoices.allTypes')}</option>
											<option value="prepayment">{t('invoices.prepayments')}</option>
											<option value="invoice">{t('invoices.invoicesLabel')}</option>
											<option value="credit">{t('invoices.creditInvoices')}</option>
										</select>
										<select 
											value={statusFilter} 
											onChange={(e) => {
												setStatusFilter(e.target.value);
												setCurrentPage(1);
											}}
										>
											<option value="all">{t('invoices.allStatuses')}</option>
											{INVOICE_STATUSES
												.filter((status) => user.role === 'admin' || status !== 'pending')
												.map((status) => (
													<option key={status} value={status}>
														{t(`invoices.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
													</option>
												))}
										</select>
										<select value={month} onChange={(e) => {
											setMonth(e.target.value);
											setCurrentPage(1);
										}}>
											<option value="">{t('common.allMonths')}</option>
											{year === '' ? (
												generateRolling12Months(locale).map((m) => (
													<option key={m.value} value={m.value}>
														{m.label}
													</option>
												))
											) : (
												Array.from({ length: 12 }, (_, i) => (
													<option key={i + 1} value={i + 1}>
														{new Date(0, i).toLocaleString(locale === 'et' ? 'et-EE' : 'en-US', { month: 'long' })}
													</option>
												))
											)}
										</select>
									<select value={year} onChange={(e) => {
										setYear(e.target.value);
										setCurrentPage(1);
									}}>
										<option value="">{t('common.allYears')}</option>
										{Array.from({ length: Math.max(1, new Date().getFullYear() - 2024 + 1) }, (_, i) => {
											const yearOption = new Date().getFullYear() - i;
											return (
											<option key={yearOption} value={yearOption}>
												{yearOption}
											</option>
												);
										})}
								 					</select>
										{/* Partner filter - only show for admin users */}
										{user.role === 'admin' && (
											<select value={partnerFilter} onChange={(e) => {
												setPartnerFilter(e.target.value);
												setCurrentPage(1);
											}}>
												<option value="">{t('common.allPartners')}</option>
												{crmUsers.map((partner) => (
													<option key={partner.user_no} value={partner.user_no}>
														{partner.firstName} {partner.lastName} {partner.partner_company_name ? `(${partner.partner_company_name})` : ''}
													</option>
												))}
											</select>
										)}
										{/* Hide CSV buttons for lawyer users */}
										{user.role !== 'lawyer' && (
											<>
												<button onClick={handleDownloadCSV} className="button button-blue">
													{t('common.csv')}
												</button>
												<button onClick={handleDownloadTransactionsCSV} className="button button-blue" style={{marginLeft: '8px'}}>
													{t('invoices.transactionsCsv')}
												</button>
											</>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
								<div className="page-content nopaginate">
									<DataTablePaginated
									data          = {invoices}
									columns       = {tableColumns}
									onTableChange = {handleTableChange}
									rowClasses    = {(row) => !['paid', 'credited', 'unpaid'].includes(row.status) ? 'invoice-unpaid' : ''}
									totalRowData  = {buildInvoiceTotalRowData(totals, fetchTotals, totalsLoading)}
									/>
								</div>
								<div className="page-footer d-flex justify-content-between align-items-center">
									<div className='filter filter-pagination d-flex align-items-center'>
										{Math.ceil(totalRecords / sizePerPage) > 0 && (
										<ReactPaginate
										containerClassName    = {'pagination'}
										activeClassName       = {'active'}
										previousLinkClassName = {'fa-regular fa-chevron-left'}
										nextLinkClassName     = {'fa-regular fa-chevron-right'}
										previousClassName     = {currentPage === 1 ? 'd-none' : 'previous'}
										nextClassName         = {currentPage === Math.ceil(totalRecords / sizePerPage) ? 'd-none' : 'next'}
										previousLabel         = {t('common.previous')}
										nextLabel             = {t('common.next')}
										breakLabel            = {t('common.ellipsis')}
										marginPagesDisplayed  = {2}
										pageRangeDisplayed    = {5}
										pageCount             = {Math.ceil(totalRecords / sizePerPage)}
										onPageChange          = {(e) => {
											const newPage = e.selected + 1;
											setCurrentPage(newPage);
										}}
										forcePage             = {currentPage - 1}
										/>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
							</>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Only show edit modal for non-lawyer users */}
			{editModalVisible && user.role !== 'lawyer' && (
				<div className="modal" onClick={() => setEditModalVisible(false)}>
					<div className="modal-content" onClick={e => e.stopPropagation()}>
					<div className="modal-header">
						<span className="modalclose" onClick={() => setEditModalVisible(false)}>&times;</span>
						<h2>{t('invoices.editTitle')}</h2>
					</div>
					<div className="modal-form">
						<form onSubmit={handlePartnerUpdate}>
							<div className="form-row col-full">
								<label>{t('invoices.relatedPartner')}</label>
								<select
									value={editInvoice.related_partner_id || ''}
									onChange={e => {
										const selectedUser = crmUsers.find(u => u.user_no === e.target.value);
										setEditInvoice({ 
											...editInvoice, 
											related_partner_id: e.target.value,
											partner_name: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : '',
											partner_company_name: selectedUser ? selectedUser.partner_company_name || '' : ''
										});
									}}
									disabled={dropdownsLoading}
								>
									<option value="">{t('invoices.selectPartner')}</option>
									{crmUsers.map((user) => (
										<option key={user._id} value={user.user_no}>
											{user.firstName} {user.lastName} ({user.role}) - {user.partner_company_name || t('invoices.noCompany')}
										</option>
									))}
								</select>
							</div>

							<div className="form-row col-full">
								<label>{t('invoices.relatedClient')}</label>
								<div className="client-search-container" style={{position: 'relative'}}>
									<input
										type="text"
										placeholder={t('invoices.searchClient')}
										value={clientSearch}
										onChange={e => handleClientSearch(e.target.value)}
										onFocus={() => {
											if (clientSearch.trim() === '' && clients.length > 0) {
												setFilteredClients(clients);
												setShowClientDropdown(true);
											} else if (filteredClients.length > 0) {
												setShowClientDropdown(true);
											}
										}}
										style={{
											width: '100%',
											paddingRight: clientSearch ? '30px' : '12px'
										}}
									/>
									{clientSearch && (
										<button
											type="button"
											onClick={clearClientSelection}
											style={{
												position: 'absolute',
												right: '8px',
												top: '50%',
												transform: 'translateY(-50%)',
												background: 'none',
												border: 'none',
												fontSize: '16px',
												cursor: 'pointer',
												color: '#999',
												padding: '0',
												width: '20px',
												height: '20px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center'
											}}
											title={t('common.clearSelection')}
										>
											×
										</button>
									)}
									{showClientDropdown && filteredClients.length > 0 && (
										<div className="client-dropdown" style={{
											position: 'absolute',
											top: '100%',
											left: 0,
											right: 0,
											maxHeight: '200px',
											overflowY: 'auto',
											backgroundColor: 'white',
											border: '1px solid #ddd',
											borderTop: 'none',
											zIndex: 1000,
											boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
										}}>
											{filteredClients.slice(0, 20).map(client => (
												<div
													key={client._id}
													className="client-option"
													onClick={() => selectClient(client)}
													style={{
														padding: '12px',
														borderBottom: '1px solid #f0f0f0',
														cursor: 'pointer',
														backgroundColor: 'white'
													}}
													onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
													onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
												>
													<div style={{fontWeight: 'bold', pointerEvents: 'none'}}>
														{client.type === 'business' && client.business_name ? (
															<>
																{client.business_name}
																<span style={{fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '8px'}}>
																	({client.firstname} {client.lastname})
																</span>
															</>
														) : (
															`${client.firstname} ${client.lastname}`
														)}
													</div>
													<div style={{fontSize: '12px', color: '#666', marginTop: '4px', pointerEvents: 'none'}}>
														{client.email}
														{client.type === 'business' && (
															<span style={{marginLeft: '8px', color: '#999'}}>{t('invoices.businessBadge')}</span>
														)}
													</div>
												</div>
											))}
											{filteredClients.length > 20 && (
												<div style={{padding: '8px 12px', fontSize: '12px', color: '#666', textAlign: 'center', backgroundColor: '#f9f9f9'}}>
													{t('common.showingFirst20')}
												</div>
											)}
										</div>
									)}
								</div>
							</div>

							<div className="form-row col-full">
								<label>{t('invoices.relatedProject')}</label>
								<div className="project-search-container" style={{position: 'relative'}}>
									<input
										type="text"
										placeholder={t('invoices.searchProject')}
										value={projectSearch}
										onChange={e => handleProjectSearch(e.target.value)}
										onFocus={() => {
											if (projectSearch.trim() === '' && projects.length > 0) {
												setFilteredProjects(projects);
												setShowProjectDropdown(true);
											} else if (filteredProjects.length > 0) {
												setShowProjectDropdown(true);
											}
										}}
										style={{
											width: '100%',
											paddingRight: projectSearch ? '30px' : '12px'
										}}
									/>
									{projectSearch && (
										<button
											type="button"
											onClick={clearProjectSelection}
											style={{
												position: 'absolute',
												right: '8px',
												top: '50%',
												transform: 'translateY(-50%)',
												background: 'none',
												border: 'none',
												fontSize: '16px',
												cursor: 'pointer',
												color: '#999',
												padding: '0',
												width: '20px',
												height: '20px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center'
											}}
											title={t('common.clearSelection')}
										>
											×
										</button>
									)}
									{showProjectDropdown && filteredProjects.length > 0 && (
										<div className="project-dropdown" style={{
											position: 'absolute',
											top: '100%',
											left: 0,
											right: 0,
											maxHeight: '200px',
											overflowY: 'auto',
											backgroundColor: 'white',
											border: '1px solid #ddd',
											borderTop: 'none',
											zIndex: 1000,
											boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
										}}>
											{filteredProjects.slice(0, 20).map(project => (
												<div
													key={project._id}
													className="project-option"
													onClick={() => selectProject(project)}
													style={{
														padding: '12px',
														borderBottom: '1px solid #f0f0f0',
														cursor: 'pointer',
														backgroundColor: 'white'
													}}
													onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
													onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
												>
													<div style={{fontWeight: 'bold', pointerEvents: 'none'}}>
														#{project.project_no} - {project.project_name}
													</div>
													<div style={{fontSize: '12px', color: '#666', marginTop: '2px', pointerEvents: 'none'}}>
														{project.client_name}
													</div>
												</div>
											))}
											{filteredProjects.length > 20 && (
												<div style={{padding: '8px 12px', fontSize: '12px', color: '#666', textAlign: 'center', backgroundColor: '#f9f9f9'}}>
													{t('common.showingFirst20')}
												</div>
											)}
											{filteredProjects.length === 0 && projectSearch.trim() !== '' && (
												<div style={{padding: '12px', fontSize: '12px', color: '#666', textAlign: 'center'}}>
													{t('common.noProjectsFound', { query: projectSearch })}
												</div>
											)}
										</div>
									)}
								</div>
							</div>

							{/* Read-only fields to show selected values */}
							<div className="form-row col-half">
								<label>{t('invoices.partnerName')}</label>
								<input 
									type="text" 
									value={editInvoice.partner_name || ''} 
									className="readonly"
									readOnly
								/>
							</div>

							<div className="form-row col-half">
								<label>{t('invoices.partnerCompany')}</label>
								<input 
									type="text" 
									value={editInvoice.partner_company_name || ''} 
									className="readonly"
									readOnly
								/>
							</div>

							<div className="form-row col-half">
								<label>{t('invoices.projectId')}</label>
								<input 
									type="text" 
									value={editInvoice.related_project_id || ''} 
									className="readonly"
									readOnly
								/>
							</div>

							<div className="form-row col-half">
								<label>{t('invoices.projectNumber')}</label>
								<input 
									type="text" 
									value={editInvoice.related_project_no || ''} 
									className="readonly"
									readOnly
								/>
							</div>

							{/* Read-only status field */}
							<div className="form-row col-full">
								<label>{t('invoices.currentStatus')}</label>
								<input 
									type="text" 
									value={editInvoice.status || ''} 
									className="readonly"
									readOnly
								/>
							</div>
				
						<div className="form-row col-full row-actions">
							<button type="button"  className="button button-large" onClick={() => setEditModalVisible(false)}>{t('common.cancel')}</button>
							<button type="submit" className="button button-large button-blue">
							{t('common.update')}
							</button>
						</div>       
						</form>
					</div>
					</div>
				</div>
				)}
				
			{/* Add Receipt Modal */}
			{receiptModalVisible && receiptInvoice && (
				<div className="modal" onClick={() => setReceiptModalVisible(false)}>
					<div className="modal-content" onClick={e => e.stopPropagation()}>
						<div className="modal-header">
							<span className="modalclose" onClick={() => setReceiptModalVisible(false)}>&times;</span>
							<h2>{t('invoices.addReceiptTitle')}</h2>
						</div>
						<div className="modal-form">
							<form onSubmit={handleAddReceipt}>
								<div className="form-row col-full">
									<label>{t('invoices.invoiceNumber')}</label>
									<input 
										type="text" 
										value={receiptInvoice.invoiceNumber || ''} 
										className="readonly"
										readOnly
									/>
								</div>
								
								<div className="form-row col-full">
									<label>{t('invoices.totalAmount')}</label>
									<input 
										type="text" 
										value={`${receiptInvoice.amount_total || 0} €`} 
										className="readonly"
										readOnly
									/>
								</div>
								
								<div className="form-row col-full">
									<label>{t('invoices.amountDue')}</label>
									<input 
										type="text" 
										value={`${maxAmount.toFixed(2)} €`} 
										className="readonly"
										readOnly
									/>
								</div>
								
								<div className="form-row col-half">
									<label>{t('invoices.paymentAmount')}</label>
									<input
										type="number"
										step="0.01"
										min="0.01"
										max={maxAmount}
										value={receiptData.amount}
										onChange={e => {
											const value = e.target.value;
											// Allow empty string or valid number input
											if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
												setReceiptData({ ...receiptData, amount: value });
											}
										}}
										onBlur={e => {
											const value = parseFloat(e.target.value);
											if (isNaN(value) || value <= 0) {
												setReceiptData({ ...receiptData, amount: maxAmount > 0 ? maxAmount.toString() : '' });
											} else if (value > maxAmount) {
												setReceiptData({ ...receiptData, amount: maxAmount.toString() });
											}
										}}
										required
									/>
									<small style={{color: '#666', marginTop: '5px', display: 'block'}}>
										{t('common.maximum', { amount: maxAmount.toFixed(2) })}
									</small>
								</div>
								
								<div className="form-row col-half">
									<label>{t('invoices.paidDate')}</label>
									<input
										type="date"
										value={receiptData.receipt_date}
										onChange={e => setReceiptData({ ...receiptData, receipt_date: e.target.value })}
										required
									/>
									<small style={{color: 'transparent', marginTop: '5px', display: 'block', height: '17px'}}>
										&nbsp;
									</small>
								</div>
								
								<div className="form-row col-full">
									<label>{t('invoices.paymentMethod')}</label>
									<select
										value={receiptData.payment_method}
										onChange={e => setReceiptData({ ...receiptData, payment_method: e.target.value })}
										required
									>
										<option value="">{t('invoices.selectPaymentMethod')}</option>
										<option value="bank_transfer_3475">{t('invoices.bankTransfer')}</option>
										<option value="card_payment_3475">{t('invoices.cardPayment')}</option>
										<option value="cash_payment">{t('invoices.cashPayment')}</option>
									</select>
								</div>
								
								<div className="form-row col-full row-actions">
									<button type="button" className="button button-large" onClick={() => setReceiptModalVisible(false)}>{t('common.cancel')}</button>
									<button type="submit" className="button button-large button-blue">
										{t('invoices.addReceiptBtn')}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
			
			{/* Tooltip */}
			{
				tooltip.visible && (
					<div
					className='d-flex flex-column info-tooltip'
					style={{
						position: 'fixed',
						top: tooltip.y + 10,
						left: tooltip.x + 10,
						zIndex: 9999,
						pointerEvents: 'none',
						width: 'auto',
						minWidth: 'auto',
						maxWidth: '120px',
						padding: '6px 10px',
						whiteSpace: 'nowrap'
					}}
					dangerouslySetInnerHTML={{__html: tooltip.text}}
					/>
				)
			}
		</div>
		


		</>
	);
};
